import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Button,
  Card,
  Form,
  Row,
  Space,
  Spinner
} from "@masterteam/mt-design-react";
import { useSelector } from "react-redux";
import { array, boolean, object } from "yup";
import { FormattedMessage, useIntl } from "react-intl";

import api from "../../../providers/api";
import { addToast } from "../../../helpers";
import useIsAr from "../../../Hooks/useIsAr";
import { downloadFile } from "../../../helpers/attachments";

import "./style.scss";

const validationSchema = object().shape({
  LevelOneObjective: object().shape({
    IsSelected: boolean(),
    Filter: object().shape({
      Strategies: array(),
      Sectors: array(),
      Year: object(),
      Quarter: object()
    })
  }),
  LevelTwoObjective: object().shape({
    IsSelected: boolean(),
    Filter: object().shape({
      Strategies: array(),
      Sectors: array(),
      Year: object(),
      Quarter: object()
    })
  }),
  OPKpisPrimary: object().shape({
    IsSelected: boolean(),
    Filter: object().shape({
      Departments: array(),
      Objectives: array(),
      Year: null,
      Quarter: null
    })
  }),
  OPKpisSecondary: object().shape({
    IsSelected: boolean(),
    Filter: object().shape({
      Departments: array(),
      Objectives: array(),
      Year: null,
      Quarter: null
    })
  })
});

const { Field } = Form;

const today = new Date();
const year = today.getFullYear();
const month = today.getMonth();
const quarter = Math.floor(month / 3) + 1;

const CEOReportFilters = ({}) => {
  const [isAr] = useIsAr();
  const intl = useIntl();
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [isWordLoading, setIsWordLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [years, setYears] = useState([]);
  const quarters = useSelector(({ theme }) => theme.Quarters.item.Items);
  const currentYear = useMemo(() => ({ Label: year, Value: year }), []);
  const currentQuarter = useMemo(
    () => quarters?.find((e) => +e?.Value === quarter),
    []
  );

  const initialValues = {
    LevelOneObjective: {
      IsSelected: true,
      Filter: {
        Strategies: [],
        Sectors: [],
        Year: currentYear,
        Quarter: currentQuarter
      }
    },
    LevelTwoObjective: {
      IsSelected: false,
      Filter: {
        Strategies: [],
        Sectors: [],
        Year: currentYear,
        Quarter: currentQuarter
      }
    },
    OPKpisPrimary: {
      IsSelected: false,
      Filter: {
        Departments: [],
        Objectives: [],
        Year: currentYear,
        Quarter: currentQuarter
      }
    },
    OPKpisSecondary: {
      IsSelected: false,
      Filter: {
        Departments: [],
        Sector: [],
        Year: currentYear,
        Quarter: currentQuarter
      }
    }
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const [strategiesRes, yearsRes] = await Promise.all([
        api.strategy.getAllWithOrgStructures({ strategyType: 1 }),
        api.dashboard.getValidYears()
      ]);
      setStrategies(strategiesRes.Data ?? []);
      setYears(
        yearsRes.Data?.map((e) => {
          return { Label: e, Value: e };
        })
      );
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values, isPdf) => {
    if (isPdf) setIsPDFLoading(true);
    else setIsWordLoading(true);

    try {
      const body = {
        LevelOneObjective: {
          IsSelected: values?.LevelOneObjective?.IsSelected,
          Filter: {
            Strategies:
              values?.LevelOneObjective?.Filter?.Strategies?.map((e) => e.ID) ??
              [],
            Sectors:
              values?.LevelOneObjective?.Filter?.Sectors?.map((e) => e.ID) ??
              [],
            Year: values?.LevelOneObjective?.Filter?.Year?.Value ?? null,
            Quarter: values?.LevelOneObjective?.Filter?.Quarter?.Value ?? null
          }
        },
        LevelTwoObjective: {
          IsSelected: values?.LevelTwoObjective?.IsSelected,
          Filter: {
            Strategies:
              values?.LevelTwoObjective?.Filter?.Strategies?.map((e) => e.ID) ??
              [],
            Sectors:
              values?.LevelTwoObjective?.Filter?.Sectors?.map((e) => e.ID) ??
              [],
            Year: values?.LevelTwoObjective?.Filter?.Year?.Value ?? null,
            Quarter: values?.LevelTwoObjective?.Filter?.Quarter?.Value ?? null
          }
        },
        OPKpisPrimary: {
          IsSelected: values?.OPKpisPrimary?.IsSelected,
          Filter: {
            Departments: [],
            Objectives: [],
            Year: null,
            Quarter: null
          }
        },
        OPKpisSecondary: {
          IsSelected: values?.OPKpisSecondary?.IsSelected,
          Filter: {
            Departments: [],
            Sector: [],
            Year: null,
            Quarter: null
          }
        }
      };

      const res = await api.report.getSectorReport(body, { isPDF: isPdf });
      const fileUrl = window.URL.createObjectURL(res);
      const dateAndTime = moment().locale("en").format("DD-MM-YYYY hh:mm");

      downloadFile({
        localURL: fileUrl,
        FileName: `${intl.formatMessage({
          id: "reports.sectors-report"
        })} - ${dateAndTime}.${isPdf ? "pdf" : "docx"}`
      });
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsPDFLoading(false);
      setIsWordLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return isLoading ? (
    <Row justify="center" align="center" style={{ height: "200px" }}>
      <Spinner size="large" />
    </Row>
  ) : (
    <Card className="filters-container">
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => (
          <>
            <Row align="center" justify="end" className="form-actions">
              <Space size="medium">
                <Button
                  loading={isPDFLoading}
                  disabled={isWordLoading || isPDFLoading}
                  // type="submit"
                  variant="primary"
                  onClick={() => {
                    setIsPDFLoading(true);
                    onSubmit(values, true);
                  }}
                >
                  {intl.formatMessage({
                    id: "reports.download-pdf"
                  })}
                </Button>

                <Button
                  loading={isWordLoading}
                  disabled={isWordLoading || isPDFLoading}
                  variant="primary"
                  // type="submit"
                  onClick={() => {
                    setIsWordLoading(true);
                    onSubmit(values, false);
                  }}
                >
                  {intl.formatMessage({
                    id: "reports.download-word"
                  })}
                </Button>
              </Space>
            </Row>

            <Space direction="vertical" gap={24} className="form-container">
              <Space direction="vertical" className="report-sections-wrapper">
                <h3>
                  <FormattedMessage id="reports-main-header" />
                </h3>

                <Row className="sections">
                  <Field
                    name="LevelOneObjective.IsSelected"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "reports.level-one-objectives"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        setFieldValue("LevelOneObjective", {
                          IsSelected: !values?.LevelOneObjective.IsSelected,
                          Filter: {
                            ...values?.LevelOneObjective?.Filter,
                            Sectors: [],
                            Year: currentYear,
                            Quarter: currentQuarter
                          }
                        });
                      }
                    }}
                    hideErrorMessage
                  />

                  <Field
                    name="LevelTwoObjective.IsSelected"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "reports.level-two-objectives"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        setFieldValue("LevelTwoObjective", {
                          IsSelected: !values?.LevelTwoObjective.IsSelected,
                          Filter: {
                            ...values?.LevelTwoObjective?.Filter,
                            Sectors: [],
                            Year: currentYear,
                            Quarter: currentQuarter
                          }
                        });
                      }
                    }}
                    hideErrorMessage
                  />

                  <Field
                    name="OPKpisPrimary.IsSelected"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "reports.main-operational-kpis"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        setFieldValue("OPKpisPrimary", {
                          IsSelected: !values?.OPKpisPrimary.IsSelected,
                          Filter: {
                            Departments: [],
                            Objectives: [],
                            Year: currentYear,
                            Quarter: currentQuarter
                          }
                        });
                      }
                    }}
                    hideErrorMessage
                  />

                  <Field
                    name="OPKpisSecondary.IsSelected"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "reports.secondary-operational-kpis"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        setFieldValue("OPKpisSecondary", {
                          IsSelected: !values?.OPKpisSecondary.IsSelected,
                          Filter: {
                            Departments: [],
                            Objectives: [],
                            Year: currentYear,
                            Quarter: currentQuarter
                          }
                        });
                      }
                    }}
                    hideErrorMessage
                  />
                </Row>
              </Space>

              {values?.LevelOneObjective.IsSelected ? (
                <Space direction="vertical" className="section-wrapper">
                  <h3>
                    <FormattedMessage id="reports.level-one-objectives" />
                  </h3>

                  <Row align="center" className="fields" gap={16}>
                    <Field
                      type="select"
                      name="LevelOneObjective.Filter.Strategies"
                      label={intl.formatMessage({
                        id: "reports.strategic-strategy"
                      })}
                      inputProps={{
                        isMulti: true,
                        options: strategies,
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.strategic-strategy"
                        }),
                        onChange: (e) => {
                          setFieldValue(
                            "LevelOneObjective.Filter.Strategies",
                            e
                          );
                          setFieldValue("LevelOneObjective.Filter.Sectors", []);

                          setFieldValue(
                            "LevelTwoObjective.Filter.Strategies",
                            e
                          );

                          setFieldValue("LevelTwoObjective.Filter.Sectors", []);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelOneObjective.Filter.Sectors"
                      label={intl.formatMessage({
                        id: "reports.sector"
                      })}
                      inputProps={{
                        isMulti: true,
                        options:
                          values?.LevelOneObjective?.Filter?.Strategies?.flatMap(
                            (strategy) => strategy.OrgStructures
                          ) || [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.sector"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelOneObjective.Filter.Sectors", e);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelOneObjective.Filter.Year"
                      label={intl.formatMessage({
                        id: "reports.year"
                      })}
                      inputProps={{
                        options: years,
                        getOptionLabel: (el) => el.Label,
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.year"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelOneObjective.Filter.Year", e);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelOneObjective.Filter.Quarter"
                      label={intl.formatMessage({
                        id: "reports.quarter"
                      })}
                      inputProps={{
                        options: quarters,
                        getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.quarter"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelOneObjective.Filter.Quarter", e);
                        }
                      }}
                      hideErrorMessage
                    />
                  </Row>
                </Space>
              ) : null}

              {values?.LevelTwoObjective.IsSelected ? (
                <Space direction="vertical" className="section-wrapper">
                  <h3>
                    <FormattedMessage id="reports.level-two-objectives" />
                  </h3>
                  <Row align="center" className="fields" gap={16}>
                    <Field
                      type="select"
                      name="LevelTwoObjective.Filter.Strategies"
                      label={intl.formatMessage({
                        id: "reports.strategic-strategy"
                      })}
                      inputProps={{
                        isMulti: true,
                        options: strategies,
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.strategic-strategy"
                        }),
                        onChange: (e) => {
                          setFieldValue(
                            "LevelTwoObjective.Filter.Strategies",
                            e
                          );

                          setFieldValue("LevelTwoObjective.Filter.Sectors", []);

                          setFieldValue(
                            "LevelOneObjective.Filter.Strategies",
                            e
                          );
                          setFieldValue("LevelOneObjective.Filter.Sectors", []);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelTwoObjective.Filter.Sectors"
                      label={intl.formatMessage({
                        id: "reports.sector"
                      })}
                      inputProps={{
                        isMulti: true,
                        options:
                          values?.LevelTwoObjective?.Filter?.Strategies?.flatMap(
                            (strategy) => strategy.OrgStructures
                          ) || [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.sector"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelTwoObjective.Filter.Sectors", e);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelTwoObjective.Filter.Year"
                      label={intl.formatMessage({
                        id: "reports.year"
                      })}
                      inputProps={{
                        options: years,
                        getOptionLabel: (el) => el.Label,
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.year"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelTwoObjective.Filter.Year", e);
                        }
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="LevelTwoObjective.Filter.Quarter"
                      label={intl.formatMessage({
                        id: "reports.quarter"
                      })}
                      inputProps={{
                        options: quarters,
                        getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.quarter"
                        }),
                        onChange: (e) => {
                          setFieldValue("LevelTwoObjective.Filter.Quarter", e);
                        }
                      }}
                      hideErrorMessage
                    />
                  </Row>
                </Space>
              ) : null}

              {values?.OPKpisPrimary.IsSelected ? (
                <Space direction="vertical" className="section-wrapper">
                  <h3>
                    <FormattedMessage id="reports.main-operational-kpis" />
                  </h3>
                  <Row align="center" className="fields" gap={16}>
                    <Field
                      type="select"
                      name="OPKpisPrimary.Filter.Sector"
                      label={intl.formatMessage({
                        id: "reports.sector"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.sector"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisPrimary.Filter.Department"
                      label={intl.formatMessage({
                        id: "reports.executive-department"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.executive-department"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisPrimary.Filter.Year"
                      label={intl.formatMessage({
                        id: "reports.year"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) => el.Label,
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.year"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisPrimary.Filter.Month"
                      label={intl.formatMessage({
                        id: "reports.month"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.month"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />
                  </Row>
                </Space>
              ) : null}

              {values?.OPKpisSecondary.IsSelected ? (
                <Space direction="vertical" className="section-wrapper">
                  <h3>
                    <FormattedMessage id="reports.secondary-operational-kpis" />
                  </h3>
                  <Row align="center" className="fields" gap={16}>
                    <Field
                      type="select"
                      name="OPKpisSecondary.Filter.Sector"
                      label={intl.formatMessage({
                        id: "reports.sector"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.sector"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisSecondary.Filter.Department"
                      label={intl.formatMessage({
                        id: "reports.executive-department"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) =>
                          isAr ? el.ArabicName : el.EnglishName,
                        getOptionValue: (el) => el.ID,
                        placeholder: intl.formatMessage({
                          id: "reports.executive-department"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisSecondary.Filter.Year"
                      label={intl.formatMessage({
                        id: "reports.year"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) => el.Label,
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.year"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />

                    <Field
                      type="select"
                      name="OPKpisSecondary.Filter.Month"
                      label={intl.formatMessage({
                        id: "reports.month"
                      })}
                      inputProps={{
                        options: [],
                        getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                        getOptionValue: (el) => el.Value,
                        placeholder: intl.formatMessage({
                          id: "reports.month"
                        }),
                        onChange: (e) => {}
                      }}
                      hideErrorMessage
                    />
                  </Row>
                </Space>
              ) : null}
            </Space>
          </>
        )}
      </Form>
    </Card>
  );
};

export default CEOReportFilters;
